import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Api from '../../../../../services/Api';
import BasePage from '../../../BasePage/BasePage';
import ImageCarousel from '../../../../atoms/ImageCarousel/ImageCarousel';
import Grid from '../../../../atoms/Grid/Grid';
import InputControl from '../../../../atoms/InputControl/InputControl';
import TagsInput from '../../../../atoms/TagsInput/TagsInput';
import CodeInput from '../../../../atoms/CodeInput/CodeInput';
import DynastySelector from '../../../../atoms/DynastySelector/DynastySelector';
import CitySelector from '../../../../atoms/CitySelector/CItySelector';
import CitationEditor from '../../../../molecules/CitationEditor/CitationEditor';
import Button from '../../../../atoms/Button/Button';
import Icon from '../../../../atoms/Icon/Icon';
import {
  fetchCharacteristicsInitAction,
  getCharacteristicSuccessAction,
  updateSelectedCharacteristicAction,
} from '../../../../../actions/instances/characteristics';
import BuildingsSelector from '../../../../atoms/BuildingsSelector/BuildingsSelector';
import extractErrors from '../../../../../helpers/errors';
import CharacteristicsTypeSelector from '../../../../atoms/CharacteristicsTypeSelector/CharacteristicsTypeSelector';
import InstanceDocumentEditor from '../../../../Organisms/InstanceDocumentEditor/InstanceDocumentEditor';
import './CreateCharacteristic.scss';

const CreateCharacteristic = () => {
  const { selectedCharacteristic } = useSelector(state => state.characteristics);
  const [characteristic, setCharacteristic] = useState(selectedCharacteristic);
  useEffect(() => {
    if (selectedCharacteristic) {
      Api.get(`instances/building-characteristics/${selectedCharacteristic.id}`).then(response => setCharacteristic(response.data));
    }
  }, []);
  let mainImage;
  let restOfImages = [];
  if (characteristic && characteristic.images) {
    if (characteristic.images.length > 0) {
      [mainImage] = characteristic.images;
      restOfImages = characteristic.images.slice(0, 1);
    }
  }
  const [imagesIds, setImagesIds] = useState(restOfImages);
  const [mainImageId, setMainImageId] = useState(mainImage);
  const [name, setName] = useState(characteristic ? characteristic.name : undefined);
  const [code, setCode] = useState(characteristic ? characteristic.code : undefined);
  const [characteristicType, setCharacteristicType] = useState(
    characteristic ? {
      id: characteristic.characteristic_type.id,
      code: characteristic.characteristic_type.code,
    } : undefined,
  );
  const [dynastyCode, setDynastyCode] = useState(
    characteristic ? characteristic.dynasty.code : undefined,
  );
  const [dynastyId, setDynastyId] = useState(
    characteristic ? characteristic.dynasty_id : undefined,
  );
  const [cityId, setCityId] = useState(
    characteristic ? characteristic.city.id : undefined,
  );
  const [cityCode, setCityCode] = useState(characteristic ? characteristic.city.code : undefined);
  const [resourcesIds, setResourcesIds] = useState(
    characteristic && characteristic.citation ? characteristic.citation.map(c => c.id) : undefined,
  );
  const [errors, setErrors] = useState(undefined);
  const [tagsIds, setTagsIds] = useState(
    characteristic && characteristic.tags ? characteristic.tags.map(t => t.id) : undefined,
  );
  const [buildingsIds, setBuildingsIds] = useState(
    characteristic && characteristic.buildings
      ? characteristic.buildings.map(x => x.id) : undefined,
  );
  const [documentsIds, setDocumentsIds] = useState(
    characteristic ? characteristic.documents.map(doc => doc.id) : [],
  );
  const [isLoading, setIsLoading] = useState(false);
  const [identifier, setIdentifier] = useState(
    characteristic ? `${characteristic.city.code}-${characteristic.dynasty.code}-${characteristic.code}` : undefined,
  );
  useEffect(() => {
    let combinedCode = '';
    if (cityCode) {
      combinedCode += `${cityCode}-`;
    }
    if (dynastyCode) {
      combinedCode += `${dynastyCode}-`;
    }
    if (code) {
      combinedCode += code;
    }
    setIdentifier(combinedCode);
  }, [cityCode, dynastyCode, code]);
  const dispatch = useDispatch();
  const history = useHistory();
  const handleSave = () => {
    if (!name) {
      setErrors('Characteristic name is required');
      return;
    }
    if (!code) {
      setErrors('Characteristic code is required');
      return;
    }
    if (!characteristicType) {
      setErrors('You must select a type for the characteristic');
      return;
    }
    if (!cityId) {
      setErrors('You must select a region for the characteristic');
      return;
    }
    if (!dynastyId) {
      setErrors('You must select a dynasty for the characteristic');
      return;
    }
    if (!imagesIds) {
      setErrors('A characteristic must have an image');
      return;
    }
    setIsLoading(true);
    const callback = (response) => {
      setIsLoading(false);
      if (response.status === 200 || response.status === 201) {
        dispatch(fetchCharacteristicsInitAction());
        dispatch(updateSelectedCharacteristicAction(response.data));
        setTimeout(() => {
          setIsLoading(false);
          history.goBack();
        }, 500);
      } else {
        const errs = extractErrors(response.data);
        setErrors([errs[0]]);
        setIsLoading(false);
      }
    };
    console.log(characteristicType);
    const data = {
      name,
      code,
      city_id: cityId,
      dynasty_id: dynastyId,
      images_ids: [mainImageId, ...imagesIds].filter(img => img !== undefined),
      characteristic_type_id: characteristicType.id,
      tags_ids: tagsIds,
      buildings_ids: buildingsIds,
      resources_ids: resourcesIds,
      documents_ids: documentsIds,
      code_suffix: characteristicType ? characteristicType.code.slice(0, 2) : '',
    };
    if (characteristic) {
      Api.patch(`instances/building-characteristics/${characteristic.id}`, data).then(response => callback(response));
    } else {
      Api.post('instances/building-characteristics', data).then(response => callback(response));
    }
  };
  return (
    <BasePage>
      <div className="create-component-container">
        <h2 className="col-md-12">Create new Characteristic</h2>
        <br />
        <Grid>
          <div className="col-md-3 left-column">
            <ImageCarousel
              initialImages={characteristic ? characteristic.images : []}
              additionalImagesAddedCallback={images => setImagesIds(images)}
              mainImageAddedCallback={imageId => setMainImageId(imageId)}
            />
            <div className="col-md-12 dynasty-section">
              <InstanceDocumentEditor
                initialDocuments={characteristic ? characteristic.documents : []}
                onFilesChanged={(files) => {
                  setDocumentsIds(files.map(f => f.id));
                }}
              />
            </div>
            <div className="tags-container">
              <label className="tag-label">Characteristic Tags</label>
              <TagsInput tags={tagsIds} placeholder="Start typing..." onChange={tags => setTagsIds(tags)} />
            </div>
          </div>
          <div className="col-md-9 content-column">
            <Grid>
              <div className="col-md-12 dynasty-section">
                <label>Characteristic Type</label>
                <CharacteristicsTypeSelector
                  selectedId={characteristic ? characteristicType.id : undefined}
                  onChanged={(characteristicItem) => {
                    setCharacteristicType(characteristicItem);
                  }}
                />
              </div>
              <div className="col-md-12 dynasty-section">
                <label>Characteristic Region</label>
                <CitySelector
                  selectedId={cityId}
                  onChanged={(city) => {
                    setCityId(city.value);
                    setCityCode(city.code);
                  }}
                />
              </div>
              <div className="col-md-12 dynasty-section">
                <label>Characteristic Dynasty</label>
                <DynastySelector
                  selectedId={dynastyId}
                  onChanged={(dynasty) => {
                    setDynastyId(dynasty.value);
                    setDynastyCode(dynasty.code);
                  }}
                />
              </div>
              <div className="col-md-12">
                <InputControl label="Characteristic name" value={name} type="text" onValueChanged={value => setName(value)} placeholder="Name..." />
              </div>
              <div className="col-md-12">
                <CodeInput endpoint="instances/code-check/building-characteristics" initialValue={code} onChange={value => setCode(value)} />
                <br />
                <small>{identifier}</small>
              </div>
              <div className="col-md-12 dynasty-section">
                <label>Characteristic Buildings</label>
                <BuildingsSelector
                  cityId={cityId}
                  dynastyId={dynastyId}
                  selectedIds={buildingsIds}
                  onChanged={(values) => {
                    const ids = values.map(val => parseInt(val.value, 10));
                    setBuildingsIds(ids);
                  }}
                />
              </div>
              <div className="col-md-12 dynasty-section">
                <CitationEditor
                  onChange={values => setResourcesIds(values)}
                  initialCitations={characteristic ? characteristic.citation : []}
                />
              </div>
              {errors && (
                <div className="col-md-12 errors-container">
                  <span className="error">{errors}</span>
                </div>
              )}
              <div className="col-md-12 actions">
                <Button
                  onInvoked={() => {
                    dispatch(getCharacteristicSuccessAction(undefined));
                    history.goBack();
                  }}
                >
                  <Icon icon="times" />
                  <span>Cancel</span>
                </Button>
                <Button onInvoked={handleSave} isLoading={isLoading}>
                  <Icon icon="save" />
                  <span>Save</span>
                </Button>
              </div>
            </Grid>
          </div>
        </Grid>
      </div>
    </BasePage>
  );
};

export default CreateCharacteristic;
